import RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
import RewardsCustomerActiveRewardsQuery from "@apps/rewards/modules/CustomerRewards/queries/RewardsCustomerActiveRewardsQuery";
import RewardsEarningRulesQuery from "@apps/rewards/modules/EarningRules/query/RewardsEarningRulesQuery";
import RewardsReferralProgramQuery from "@apps/rewards/modules/ReferralProgram/queries/RewardsReferralProgramQuery";
import RewardsSpendingRulesQuery from "@apps/rewards/modules/SpendingRules/query/RewardsSpendingRulesQuery";
import RewardsBoostCampaignQuery from "@apps/rewards/queries/RewardsBoostCampaignQuery";
import RewardsCustomerBirthdayQuery from "@apps/rewards/queries/RewardsCustomerBirthdayQuery";
import RewardsNewsletterSubscriptionQuery from "@apps/rewards/queries/RewardsNewsletterSubscriptionQuery";
import RewardsVerificationTokenQuery from "@apps/rewards/queries/RewardsVerificationTokenQuery";
import GrowaveConfigurationEntity from "@lib/GrowaveConfigurationProvider/GrowaveConfigurationEntity";
import type { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
import { ShopBrandInfoProviderInterface } from "@lib/ShopBrandInfoProvider/ShopBrandInfoProviderInterface";
import { InjectionKey } from "@lib/svelte-typed-context";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { CartQuery } from "@modules/cart/queries/CartQuery";
import { CartService } from "@modules/cart/services/CartService";
import ApplyDiscountAvailabilityProvider from "@modules/common_module/ApplyDiscountAvailabilityProvider/ApplyDiscountAvailabilityProvider";
import CartAvailabilityProvider from "@modules/common_module/CartAvailabilityProvider/CartAvailabilityProvider";
import type { DesignModeProviderInterface } from "@modules/common_module/DesignModeProvider/DesignModeProviderInterface";
import LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
import { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
import ShopifyDiscountQuery from "@modules/discount/query/ShopifyDiscountQuery";
import { DiscountService } from "@modules/discount/service/DiscountService";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
import HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import { Observable } from "rxjs";

import { RewardsTranslationNamespaces } from "./constants/constants";
import RewardsCurrencyFormatter from "./lib/RewardsCurrencyFormatter";
import RewardsCustomerDelayedRewardsQuery from "./modules/CustomerRewards/queries/RewardsCustomerDelayedRewardsQuery";
import RewardsCustomerRewardsDiscountService from "./modules/CustomerRewards/services/RewardsCustomerRewardsDiscountService";
import RewardsCustomerRewardsService from "./modules/CustomerRewards/services/RewardsCustomerRewardsService";
import RewardsEarningRulesService from "./modules/EarningRules/service/RewardsEarningRulesService";
import RewardsFaqQuery from "./modules/Faq/query/RewardsFaqQuery";
import RewardsFaqService from "./modules/Faq/service/RewardsFaqService";
import RewardsHistoryQuery from "./modules/History/query/RewardsHistoryQuery";
import RewardsHistoryService from "./modules/History/service/RewardsHistoryService";
import RewardsProgramAccountQuery from "./modules/ProgramAccount/query/RewardsProgramAccountQuery";
import RewardsProgramAccountService from "./modules/ProgramAccount/service/RewardsProgramAccountService";
import RewardsQRCodeQuery from "./modules/QRCode/RewardsQRCodeQuery";
import RewardsQRCodeService from "./modules/QRCode/RewardsQRCodeService";
import RewardsReferralProgramService from "./modules/ReferralProgram/services/RewardsReferralProgramService";
import RewardsSpendingRulesService from "./modules/SpendingRules/service/RewardsSpendingRulesService";
import RewardsStorefrontValuesService from "./modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsVipTierProgressQuery from "./modules/VipTiers/queries/RewardsVipTierProgressQuery";
import RewardsVipTiersQuery from "./modules/VipTiers/queries/RewardsVipTiersQuery";
import RewardsVipTiersService from "./modules/VipTiers/service/RewardsVipTiersService";
import RewardsBoostCampaignService from "./services/RewardsBoostCampaignService";
import RewardsCustomerBirthdayService from "./services/RewardsCustomerBirthdayService";
import RewardsCustomerEligibilityService from "./services/RewardsCustomerEligibilityService/RewardsCustomerEligibilityService";
import RewardsNewsletterSubscriptionService from "./services/RewardsNewsletterSubscriptionService";
import RewardsTrackEventService from "./services/RewardsTrackEventService/RewardsTrackEventService";
import RewardsVerificationTokenService from "./services/RewardsVerificationTokenService";
import RewardsModalManager from "./utils/RewardsModalManager/RewardsModalManager";
import RewardsPopUpRootWidgetCardManager from "./widgets/rewards_popup_root_widget/utils/RewardsPopUpWidgetCardManager/RewardsPopUpRootWidgetCardManager";
import RewardsPopUpWidgetLauncherManager from "./widgets/rewards_popup_widget/utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";

export const rewardsContextToken: InjectionKey<{
    //programAccount
    rewardsProgramAccountService: RewardsProgramAccountService;
    rewardsProgramAccountQuery: RewardsProgramAccountQuery;

    //referralProgram
    rewardsReferralProgramService: RewardsReferralProgramService;
    rewardsReferralProgramQuery: RewardsReferralProgramQuery;

    //earningRules
    rewardsEarningRulesService: RewardsEarningRulesService;
    rewardsEarningRulesQuery: RewardsEarningRulesQuery;

    //spendingRules
    rewardsSpendingRulesService: RewardsSpendingRulesService;
    rewardsSpendingRulesQuery: RewardsSpendingRulesQuery;

    //faq
    rewardsFaqService: RewardsFaqService;
    rewardsFaqQuery: RewardsFaqQuery;

    //vipTiers
    rewardsVipTiersService: RewardsVipTiersService;
    rewardsVipTiersQuery: RewardsVipTiersQuery;
    rewardsVipTierProgressQuery: RewardsVipTierProgressQuery;

    //customerRewards
    rewardsCustomerRewardsService: RewardsCustomerRewardsService;
    rewardsCustomerRewardsDiscountService: RewardsCustomerRewardsDiscountService;
    rewardsCustomerActiveRewardsQuery: RewardsCustomerActiveRewardsQuery;
    rewardsCustomerDelayedRewardsQuery: RewardsCustomerDelayedRewardsQuery;

    //boostCampaign
    rewardsBoostCampaignService: RewardsBoostCampaignService;
    rewardsBoostCampaignQuery: RewardsBoostCampaignQuery;

    //customerBirthday
    rewardsCustomerBirthdayService: RewardsCustomerBirthdayService;
    rewardsCustomerBirthdayQuery: RewardsCustomerBirthdayQuery;

    //newsletterSubscription
    rewardsNewsletterSubscriptionService: RewardsNewsletterSubscriptionService;
    rewardsNewsletterSubscriptionQuery: RewardsNewsletterSubscriptionQuery;

    //history
    rewardsHistoryService: RewardsHistoryService;
    rewardsHistoryQuery: RewardsHistoryQuery;

    //tokenManagerModule
    tokenManagerModule: TokenManagerModule;

    //qrCode
    rewardsQRCodeService: RewardsQRCodeService;
    rewardsQRCodeQuery: RewardsQRCodeQuery;

    //verificationToken
    rewardsVerificationTokenService: RewardsVerificationTokenService;
    rewardsVerificationTokenQuery: RewardsVerificationTokenQuery;

    //customerEligibilityService
    rewardsCustomerEligibilityService: RewardsCustomerEligibilityService;

    //trackEvent
    rewardsTrackEventService: RewardsTrackEventService;

    //rewardsAmountFormatter
    rewardsAmountFormatter: RewardsAmountFormatter;

    //locationController
    locationController: LocationControllerInterface;

    //eventsCollector
    eventsCollector: AnalyticsEventsCollectorInterface;

    //cartService
    cartService: CartService;
    cartQuery: CartQuery;

    //isCustomer
    isCustomer$: Observable<boolean>;

    //namespace
    rewardsPopUpWidgetNamespace: RewardsTranslationNamespaces;

    //modalManager
    rewardsModalManager: RewardsModalManager;

    //shopifyDiscount
    shopifyDiscountApiService: DiscountService;
    shopifyDiscountQuery: ShopifyDiscountQuery;

    //dynamicImportManager
    dynamicImportManager: DynamicImportManager;

    //launcherManager
    rewardsPopUpWidgetLauncherManager: RewardsPopUpWidgetLauncherManager;

    //cardManager
    rewardsPopUpRootWidgetCardManager: RewardsPopUpRootWidgetCardManager;

    //gwI18n
    gwI18n: GwI18n;

    //localizationInfoProvider
    localizationInfoProvider: LocalizationInfoProviderInterface;

    //pointsName
    pointsName$: Observable<string>;

    //rewardsCurrencyFormatter
    rewardsCurrencyFormatter: RewardsCurrencyFormatter;

    //sanitizer
    sanitizer: HTMLSanitizerInterface;

    //growaveConfiguration
    growaveConfiguration: GrowaveConfigurationEntity;

    //urlsBuilder
    urlsBuilder: URLsBuilder;

    //applyDiscountAvailabilityProvider
    applyDiscountAvailabilityProvider: ApplyDiscountAvailabilityProvider;

    //cartAvailabilityProvider
    cartAvailabilityProvider: CartAvailabilityProvider;

    //rewardsStorefrontValues
    rewardsStorefrontValuesService: RewardsStorefrontValuesService;

    //shopBrandInfoProvider
    shopBrandInfoProvider: ShopBrandInfoProviderInterface;

    //designModeProvider
    designModeProvider: DesignModeProviderInterface;
}> = Symbol("rewardsContextToken");
